<template>
  <div class="forum-wrapper">
    <v-container fluid class="forum-dashboard-container">
      <v-row class="mb-3">
        <v-col cols="12" class="d-flex align-center justify-space-between">
          <h2 class="text-h5 font-weight-bold mb-0">Forum</h2>
          
          <!-- Button Group -->
          <div class="button-group">
            <!-- Managers see "Create Category" on the LEFT -->
            <v-btn
              small
              v-if="isManager"
              color="primary"
              @click="goCreateCategory"
            >
              <span class="button-text">Create Category</span>
            </v-btn>
            <!-- Everyone sees "Create New Thread" on the RIGHT -->
            <v-btn
              small
              color="success"
              class="ml-2"
              @click="goCreateThread"
            >
              <span class="button-text">Create New Thread</span>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <!-- Minimalistic list of categories -->
          <div
            v-for="cat in categories"
            :key="cat.id"
            class="mb-4 pa-4 forum-card"
          >
            <div class="d-flex justify-space-between align-center mb-2">
              <h3 class="text-h6 font-weight-bold m-0">{{ cat.name }}</h3>
              <!-- Category Edit/Delete only for managers -->
              <div v-if="isManager" class="category-actions">
                <v-btn color="warning" small @click="goEditCategory(cat.id)">
                  Edit
                </v-btn>
                <v-btn color="error" small class="ml-2" @click="goDeleteCategory(cat.id)">
                  Delete
                </v-btn>
              </div>
            </div>
            <div class="text-body-2 mb-3" v-if="cat.description">
              {{ cat.description }}
            </div>
            <div class="threads-list">
              <div
                v-for="thread in cat.threads"
                :key="thread.id"
                class="thread-item"
                @click="goThreadDetail(thread.id)"
              >
                <div class="font-weight-medium">
                  {{ thread.title }}
                </div>
                <div class="text-caption text-muted">
                  Created by {{ thread.created_by.username }} on {{ formatDate(thread.created_at) }}
                </div>
              </div>
              <div
                v-if="!cat.threads || cat.threads.length === 0"
                class="text-subtitle-2 text-muted mt-2"
              >
                No threads in this category yet.
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import api from "@/plugins/axios";
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { storeToRefs } from "pinia";

export default {
  name: "ForumDashboard",
  setup() {
    const router = useRouter();
    const categories = ref([]);
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);

    const isManager = computed(() =>
      user.value && ["manager", "supermanager"].includes(user.value.role)
    );

    async function fetchCategories() {
      try {
        const res = await api.get("/api/forum-categories/");
        categories.value = res.data;
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    }

    function goCreateCategory() {
      router.push("/forum/category/create");
    }
    function goEditCategory(id) {
      router.push(`/forum/category/${id}/edit`);
    }
    function goDeleteCategory(id) {
      router.push(`/forum/category/${id}/delete`);
    }
    function goCreateThread() {
      router.push("/forum/create");
    }
    function goThreadDetail(threadId) {
      router.push(`/forum/${threadId}`);
    }
    function formatDate(dt) {
      return new Date(dt).toLocaleString();
    }

    watch(
      () => user.value?.active_bar,
      (newBar, oldBar) => {
        if (newBar !== oldBar) fetchCategories();
      },
      { immediate: true }
    );

    onMounted(() => {
      setTimeout(() => window.dispatchEvent(new Event("resize")), 200);
      fetchCategories();
    });

    return {
      categories,
      isManager,
      formatDate,
      goCreateCategory,
      goEditCategory,
      goDeleteCategory,
      goCreateThread,
      goThreadDetail,
    };
  },
};
</script>

<style scoped>
.forum-wrapper {
  overflow: hidden;
}

.forum-dashboard-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  padding: 20px;
  min-height: 100vh;
  position: relative;
}

.forum-card {
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
}

.category-actions {
  display: flex;
  align-items: center;
}

.thread-item {
  cursor: pointer;
  padding: 0.5rem 0;
  border-top: 1px solid #f0f0f0;
}
.thread-item:first-of-type {
  border-top: none;
}
.thread-item:hover {
  background-color: #f6f9fc;
}

/* ——— Button‑group styling ——— */
.button-group {
  display: flex;
  align-items: center;
  /* allow wrapping if things get *really* narrow */
  flex-wrap: wrap;
  gap: 8px;
}

/* only clamp/ellipsis on truly small screens */
@media (max-width: 768px) {
  .button-group .v-btn {
    font-size: 0.75rem;
    padding: 4px 8px;
  }
  .button-group .button-text {
    display: inline-block;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>